import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { LanguageToggle } from '../components/LanguageToggle'
import { ConfigDiagnostic } from '../components/ConfigDiagnostic'
import { Logo } from '../components/Logo'

export function LoginScreen() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<string | null>(null)

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setDetail(null)

    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(t('login_error_email'))
      return
    }

    setState('sending')
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        // Le lien doit revenir sur l'application installee, a la racine.
        emailRedirectTo: window.location.origin,
        // Aucun compte ne se cree tout seul : les trois comptes sont
        // crees par le proprietaire dans Supabase.
        shouldCreateUser: false,
      },
    })

    if (sendError) {
      setState('idle')
      // Chaque panne a sa propre cause. Les confondre toutes dans un
      // « verifiez votre connexion » rend n'importe quelle panne
      // impossible a diagnostiquer, surtout depuis Mbour au telephone.
      const raw = `${sendError.message} ${sendError.status ?? ''}`.toLowerCase()

      if (raw.includes('signups not allowed') || raw.includes('user not found')) {
        setError(t('login_error_no_account'))
      } else if (raw.includes('rate limit') || sendError.status === 429) {
        setError(t('login_error_rate_limit'))
      } else if (raw.includes('redirect') || raw.includes('not allowed')) {
        setError(t('login_error_redirect'))
      } else if (raw.includes('error sending') || raw.includes('smtp')) {
        setError(t('login_error_mail'))
      } else {
        setError(t('login_error_generic'))
      }

      // Conserve la cause exacte, repliee, pour ne plus jamais avoir a
      // deviner ce qui a echoue.
      setDetail(`${sendError.status ?? ''} ${sendError.message}`.trim())
      return
    }
    setState('sent')
  }

  return (
    <div className="login">
      <div className="login__top">
        <LanguageToggle />
      </div>

      <div className="login__body">
        {/* La marque, batiment et nom, comme sur le logo. */}
        <Logo onDark />
        <p className="login__sub">
          {t('app_subtitle')} · {t('app_tagline')}
        </p>

        {state === 'sent' ? (
          <div className="card">
            <h2>{t('login_sent_title')}</h2>
            <p>{t('login_sent_body', { email: email.trim() })}</p>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setState('idle')}
            >
              {t('login_sent_again')}
            </button>
          </div>
        ) : (
          <form className="card" onSubmit={send}>
            <h2>{t('login_title')}</h2>
            <p>{t('login_intro')}</p>

            <label className="field">
              <span className="field__label">{t('login_email_label')}</span>
              <input
                className="field__input"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state === 'sending'}
                required
              />
            </label>

            {error && (
              <div className="alert alert--error">
                <p>{error}</p>
                {detail && (
                  <details className="alert__detail">
                    <summary>{t('login_error_detail')}</summary>
                    <code>{detail}</code>
                  </details>
                )}
              </div>
            )}

            <button className="btn btn--primary" type="submit" disabled={state === 'sending'}>
              {state === 'sending' ? t('login_sending') : t('login_send')}
            </button>
          </form>
        )}

        <ConfigDiagnostic />
      </div>
    </div>
  )
}
